import {
  GbgrundbuchgeschaeftService,
  GrundbuchgeschaeftRestListEntryMeta,
  GrundbuchgeschaeftSearchResponseMeta
} from '../../../core/gb-api';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CapAlertService } from '../../../core/extension/exceptions/cap-alert.service';
import { ComparatorService } from '../../../shared/services/comparator-service';
import { grundbuchgeschaeftSearchModeName } from '../grundbuchgeschaeft.constants';
import { AsyncPipe, Location, NgTemplateOutlet } from '@angular/common';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { LinkComponent, NavTarget } from '../../../shared/components/link/link.component';
import { FormatConfigService } from '../../../core/services/format-config.service';
import { DokulinkItemBuilderFactory } from '../../../shared/components/dokulink/dokulinkItemBuilderFactory';
import { AbstractComponent } from '../../../shared/components/abstract-component/abstract.component';
import { ListLoaderPreloaded } from '../../../shared/components/list-loader/list-loader-preloaded';
import { ListLoader } from '../../../shared/components/list-loader/list-loader';
import { TableSortModel } from '../../../shared/tablesort/tableSortModel';
import { SearchStateService } from '../../../shared/services/search-state.service';
import { SearchMode } from '../../../shared/enum/searchMode';
import { ContentHeaderComponent } from '../../../shared/components/content-header/content-header.component';
import { TitleComponent } from '../../../shared/components/title/title.component';
import { TitleFunctionbarComponent } from '../../../shared/components/title-functionbar/title-functionbar.component';
import { FunctionbarSearchresultButtonsComponent } from '../../../shared/components/title-functionbar/functionbar-searchresult-buttons/functionbar-searchresult-buttons.component';
import { ListLoaderWrapperComponent } from '../../../shared/components/list-loader-wrapper/list-loader-wrapper.component';
import { ColumnHeaderComponent } from '../../../shared/tablesort/sort-icon/column-header.component';
import { KompaktGrundbuchgeschaeftComponent } from '../../../shared/kompaktdarstellung/grundbuchgeschaeft/kompakt-grundbuchgeschaeft.component';
import { KompaktStammpersonComponent } from '../../../shared/kompaktdarstellung/stammperson/kompakt-stammperson.component';
import { DokulinkComponent } from '../../../shared/components/dokulink/dokulink.component';
import { CapSpinnerSmallComponent } from '../../../shared/components/cap-spinner-small/cap-spinner-small.component';
import { DatumPipe } from '../../../shared/pipes/simple/datum.pipe';
import { TooltipPersonstammPipe } from '../../../shared/pipes/simple/tooltip-personstamm.pipe';
import { CmisComponent } from '../../../shared/components/cmis/cmis.component';
import { FormatTimepointPipe } from '../../../shared/pipes/format/format-timepoint.pipe';

@Component({
  selector: 'cap-grundbuchgeschaeft-list',
  templateUrl: './grundbuchgeschaeft-list.component.html',
  styleUrls: ['./grundbuchgeschaeft-list.component.scss'],
  imports: [
    ContentHeaderComponent,
    TitleComponent,
    TitleFunctionbarComponent,
    FunctionbarSearchresultButtonsComponent,
    ListLoaderWrapperComponent,
    NgTemplateOutlet,
    ColumnHeaderComponent,
    LinkComponent,
    KompaktGrundbuchgeschaeftComponent,
    KompaktStammpersonComponent,
    DokulinkComponent,
    CapSpinnerSmallComponent,
    TranslatePipe,
    DatumPipe,
    TooltipPersonstammPipe,
    AsyncPipe,
    CmisComponent,
    FormatTimepointPipe
  ]
})
export class GrundbuchgeschaeftListComponent extends AbstractComponent implements OnInit {
  navTargets = NavTarget;

  grundbuchgeschaeftListLoader: ListLoader<GrundbuchgeschaeftRestListEntryMeta>;
  grundbuchgeschaeftResponse: GrundbuchgeschaeftSearchResponseMeta;

  pageSize = 15;

  tableSortModel = new TableSortModel<GrundbuchgeschaeftRestListEntryMeta>('table', this.comparatorService);

  constructor(
    private grundbuchgeschaeftService: GbgrundbuchgeschaeftService,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private capAlertService: CapAlertService,
    private location: Location,
    private stateService: SearchStateService,
    protected comparatorService: ComparatorService,
    protected appformatConfigService: FormatConfigService,
    protected dokuLinkBuilderFactory: DokulinkItemBuilderFactory
  ) {
    super();
    keysToTranslate<string>(['Anmelder', 'Dokumente', 'Eingangsdatum', 'gefundeneGrundbuchgeschaefte', 'Grundbuchgeschäft']);

    this.tableSortModel
      .addColumn('timePoint', (a, b) => this.comparatorService.geschaeftComparator(a.timePoint, b.timePoint))
      .switchSortDirection();
    this.tableSortModel.addColumn('eingangTagebuch', (a, b) =>
      this.comparatorService.dateComparator(a.eingangTagebuch.toString(), b.eingangTagebuch.toString())
    );
    this.tableSortModel.addColumn('status', (a, b) => this.comparatorService.translatedStringComparator(a.status, b.status));
    this.tableSortModel.addColumn('anmelder', (a, b) => this.comparatorService.personListTyp02Comparator(a.anmelder, b.anmelder));
  }

  ngOnInit() {
    this.route.params.subscribe((params) => {
      const searchMode: SearchMode = params[grundbuchgeschaeftSearchModeName];

      this.route.queryParams.subscribe((param) => {
        const request = JSON.parse(param['request']);
        this.stateService.lastSearchMode = searchMode;
        this.loadGeschaefte(request, searchMode);
      });
    });
  }

  private loadGeschaefte(request: any, searchMode: string) {
    this.getGeschaeftListSubscription(request)[searchMode].subscribe(
      (response) => {
        this.loadGeschaeftFrom(response);
      },
      (err) => {
        this.handleError(err);
      }
    );
  }

  private handleError(err) {
    if (err.status === 403) {
      this.router.navigate(['/security/forbidden']);
    } else {
      throw err;
    }
  }

  private loadGeschaeftFrom(response) {
    this.grundbuchgeschaeftResponse = response;
    if (response.recordsFound === 0) {
      this.capAlertService.info(this.translate.instant('KeineResultateGefunden'), undefined, true);
      this.navigateBackToSearch();
    }
    this.grundbuchgeschaeftListLoader = new ListLoaderPreloaded<GrundbuchgeschaeftRestListEntryMeta>(this.pageSize, 1, response.results);
    this.grundbuchgeschaeftListLoader.totalItemsFound = response.recordsFound;
    this.grundbuchgeschaeftListLoader.applySortModel(this.tableSortModel);

    this.addLoader(this.grundbuchgeschaeftListLoader);
  }

  private getGeschaeftListSubscription(request) {
    return {
      searchStandard: this.grundbuchgeschaeftService.searchGbGeschaeftStandard(request),
      searchFachId: this.grundbuchgeschaeftService.searchGbGeschaeftEgbtbid(request)
    };
  }

  navigateBackToSearch() {
    this.location.back();
  }
}
